import { useState, useEffect, useRef } from "react";
import { Upload, Loader2, CheckCircle2, FileText, Trash2, ExternalLink, User, Store, Send, Bell, Link2, Copy, AlertCircle, Info, RefreshCw, FileSignature, QrCode, Smartphone, Camera, Sparkles, Settings, Plus } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import api from "../../../lib/api";
import { previewPdfResponse } from "../../../lib/pdfPreview";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Textarea } from "../../ui/textarea";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";
import { useGearsVisible } from "../../../lib/gearVisibility";
import { FinancePartnerFavorites } from "./FinancePartnerFavorites";

const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDateDE = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return String(iso).slice(0, 10);
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Jul-2026 (User-Wunsch) — Zahnrad: Regeln („wann wird was gebraucht") ansehen
// und je Beleg-Typ einstellen: Automatisch (Regel) / Immer / Nie.
function BelegeSettingsPanel({ onChanged }) {
    const [catalog, setCatalog] = useState(null);
    const [aiEnabled, setAiEnabled] = useState(true);
    const [openRule, setOpenRule] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/dealer/belege-settings")
            .then((r) => { setCatalog(r.data?.catalog || []); setAiEnabled(r.data?.ai_enabled !== false); })
            .catch(() => toast.error("Beleg-Einstellungen konnten nicht geladen werden"));
    }, []);
    const patch = async (body) => {
        setSaving(true);
        try {
            await api.patch("/dealer/belege-settings", body);
            await onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSaving(false); }
    };
    const setMode = async (code, mode) => {
        const next = (catalog || []).map((c) => (c.code === code ? { ...c, mode } : c));
        setCatalog(next);
        const modes = {};
        next.forEach((c) => { if (c.mode !== "auto") modes[c.code] = c.mode; });
        await patch({ modes });
    };
    const setRule = (code, text) => setCatalog((prev) => prev.map((c) => (c.code === code ? { ...c, ai_rule: text } : c)));
    const saveRules = async () => {
        const ai_rules = {};
        (catalog || []).forEach((c) => { if ((c.ai_rule || "").trim()) ai_rules[c.code] = c.ai_rule.trim(); });
        await patch({ ai_rules });
        toast.success("KI-Prüfregeln gespeichert — gelten für alle Standorte");
    };
    const toggleAi = async () => {
        const v = !aiEnabled;
        setAiEnabled(v);
        await patch({ ai_enabled: v });
    };
    if (!catalog) return <div className="flex items-center gap-2 text-sm text-gray-500 py-4 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Einstellungen werden geladen…</div>;
    return (
        <div className="border border-gray-200 rounded-sm" data-testid="belege-settings-panel">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Beleg-Anforderungen — wann wird was gebraucht?</span>
                <label className="flex items-center gap-1.5 cursor-pointer text-[11px] font-bold text-gray-600" title="Jeder Upload wird im Hintergrund per KI geprüft (lesbar? kein weißes Blatt? richtiges Dokument?) — Uploads laufen parallel weiter.">
                    <input type="checkbox" checked={aiEnabled} onChange={toggleAi} className="w-3.5 h-3.5 accent-orange-500" data-testid="belege-ai-toggle" />
                    <Sparkles className="w-3.5 h-3.5 text-orange-500" /> KI-Prüfung nach Upload
                </label>
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
            </div>
            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                {catalog.map((c) => (
                    <div key={c.code} className="px-3 py-2" data-testid={`belege-setting-${c.code}`}>
                        <div className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="text-[12px] font-bold text-gray-800 truncate">{c.label}
                                    <span className={`ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${c.uploader === "seller" ? "bg-gray-100 text-gray-600" : "bg-blue-50 text-blue-700"}`}>{c.uploader === "seller" ? "Verkäufer" : "Kunde"}</span>
                                </div>
                                <div className="text-[11px] text-gray-500">{c.rule}</div>
                            </div>
                            <select
                                value={c.mode}
                                onChange={(e) => setMode(c.code, e.target.value)}
                                className="h-8 text-xs border-2 border-gray-200 rounded-sm px-1.5 font-bold shrink-0"
                                data-testid={`belege-setting-mode-${c.code}`}
                            >
                                <option value="auto">Automatisch (Regel)</option>
                                <option value="immer">Immer anfordern</option>
                                <option value="nie">Nie anfordern</option>
                            </select>
                            {c.mode === "auto" && (
                                <span className="shrink-0 text-gray-400 hover:text-gray-700 cursor-help" title={`Regel: ${c.rule}`} data-testid={`belege-setting-info-${c.code}`}>
                                    <Info className="w-4 h-4" />
                                </span>
                            )}
                            <button type="button" onClick={() => setOpenRule(openRule === c.code ? null : c.code)}
                                className={`shrink-0 p-1 rounded-sm border ${openRule === c.code || (c.ai_rule || "").trim() ? "text-orange-600 border-orange-300 bg-orange-50" : "text-gray-400 border-transparent hover:text-gray-700 hover:border-gray-200"}`}
                                title="KI-Prüfregeln für diesen Beleg hinterlegen (werden nach jedem Upload automatisch mitgeprüft)"
                                data-testid={`belege-setting-airule-gear-${c.code}`}>
                                <Settings className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        {openRule === c.code && (
                            <div className="mt-2 bg-orange-50/60 border border-orange-200 rounded-sm p-2" data-testid={`belege-setting-airule-panel-${c.code}`}>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-orange-700 flex items-center gap-1 mb-1"><Sparkles className="w-3 h-3" /> KI-Prüfregeln für „{c.label}"</div>
                                <textarea
                                    value={c.ai_rule || ""}
                                    onChange={(e) => setRule(c.code, e.target.value)}
                                    onBlur={saveRules}
                                    rows={2}
                                    maxLength={800}
                                    placeholder="z. B. Unterschrift muss sichtbar sein; Datum darf nicht älter als 3 Monate sein…"
                                    className="w-full text-xs border border-orange-200 rounded-sm px-2 py-1.5 bg-white"
                                    data-testid={`belege-setting-airule-input-${c.code}`}
                                />
                                <div className="text-[10px] text-gray-500">Standard-Prüfung (immer aktiv): lesbar, kein weißes/unleserliches Blatt, erkennbarer Zusammenhang zum Dokument. Speichert beim Verlassen des Feldes.</div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <FinancePartnerFavorites />
            <p className="text-[10px] text-gray-400 px-3 py-2 border-t border-gray-100">„Automatisch" = die Regel entscheidet je Vorgang (Zahlungsart, Kundentyp …). Diese Einstellungen gelten für <b>alle Standorte und den Hauptsitz</b> Ihres Unternehmens.</p>
        </div>
    );
}

// ---------- „Upload Belege" — bedingtes Dokumenten-Upload-Portal ------------
// Jun-2026 (User-Wunsch): Stellt je nach Zahlungsart/Fahrzeug genau die Belege
// bereit, die zutreffen (Kunden- bzw. Verkäufer-Upload). Verkäufer kann hier
// intern hochladen; das öffentliche Kunden-Portal folgt in Phase 2.
function BelegRow({ listingId, doc, onChanged, portalUrl }) {
    const [busy, setBusy] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);
    const camRef = useRef(null);

    const doUpload = async (fileList) => {
        // Jul-2026 (User-Wunsch) — MEHRERE Dateien/Fotos je Beleg möglich
        // (z. B. Ausweis-Seiten, mehrseitige Verträge) — werden angehängt.
        const files = Array.from(fileList || []).filter(Boolean);
        if (!files.length) return;
        for (const f of files) {
            if (f.size > 10 * 1024 * 1024) { toast.error(`„${f.name}" zu groß (max 10 MB)`); return; }
        }
        setBusy(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("code", doc.code);
                fd.append("uploader", "seller");
                fd.append("file", file);
                await api.post(`/listings/${listingId}/dispo/belege/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success(files.length > 1 ? `${files.length} Dateien zu „${doc.label}" hochgeladen` : `„${doc.label}" hochgeladen`);
            await onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
        } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; if (camRef.current) camRef.current.value = ""; }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (busy) return;
        const files = e.dataTransfer?.files;
        if (files?.length) doUpload(files);
    };

    const doDelete = async (at = null) => {
        setBusy(true);
        try {
            await api.delete(`/listings/${listingId}/dispo/belege/${doc.code}${at ? `?at=${encodeURIComponent(at)}` : ""}`);
            toast.message(at ? "Seite entfernt" : `„${doc.label}" zurückgesetzt`);
            await onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
        } finally { setBusy(false); }
    };

    const qrValue = portalUrl ? `${portalUrl}?doc=${encodeURIComponent(doc.code)}` : "";
    // QR bleibt auch nach dem 1. Upload sichtbar — Kunde kann weitere Seiten fotografieren.
    const canQr = portalUrl && !doc.digitally_signed;
    const files = doc.files || [];

    return (
        <div
            onDragOver={(e) => { if (!doc.digitally_signed) { e.preventDefault(); setDragOver(true); } }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`flex items-center gap-2 px-3 py-2 border rounded-sm transition-colors ${
                (doc.uploaded || doc.digitally_signed || doc.required === false) ? "bg-emerald-50 border-emerald-200"
                : dragOver ? "bg-orange-50 border-orange-400 border-dashed"
                : "bg-white border-gray-200"
            }`}
            data-testid={`beleg-row-${doc.code}`}
        >
            {/* Jul-2026 (Testfix) — Inputs IMMER gerendert, damit nach dem 1. Upload
                weitere Seiten/Fotos angehängt werden können (multiple möglich). */}
            <input ref={inputRef} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={(e) => doUpload(e.target.files)} data-testid={`beleg-input-${doc.code}`} />
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => doUpload(e.target.files)} data-testid={`beleg-camera-input-${doc.code}`} />
            <FileText className={`w-4 h-4 shrink-0 ${(doc.uploaded || doc.digitally_signed || doc.required === false) ? "text-emerald-600" : "text-gray-400"}`} />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-gray-800 truncate">{doc.label}</div>
                {doc.note ? <div className="text-[11px] text-gray-500">{doc.note}</div> : null}
                {doc.uploaded ? (
                    <div className="text-[11px] text-emerald-700 flex items-center gap-1 mt-0.5" data-testid={`beleg-done-${doc.code}`}>
                        <CheckCircle2 className="w-3 h-3" /> Erledigt · {fmtDateDE(doc.uploaded_at)}
                        {doc.pages > 1 && <span className="text-gray-500 font-bold">· {doc.pages} Seiten/Dateien</span>}
                    </div>
                ) : doc.required === false && !doc.digitally_signed ? (
                    <div className="text-[11px] text-emerald-700 flex items-center gap-1 mt-0.5" data-testid={`beleg-optional-ok-${doc.code}`}>
                        <CheckCircle2 className="w-3 h-3" /> Optional — gilt als erledigt, blockiert Fortschritt &amp; Freigabe nicht
                    </div>
                ) : !doc.digitally_signed ? (
                    <div className="text-[10px] text-gray-400 mt-0.5 hidden sm:block">Datei(en) hierher ziehen, hochladen oder QR scannen — mehrere Seiten/Fotos möglich</div>
                ) : null}
                {/* Jul-2026 (User-Wunsch) — Seitenliste bei Mehrseiten-Uploads: einzeln ansehen/löschen. */}
                {doc.uploaded && files.length > 1 && (
                    <div className="mt-1 space-y-0.5" data-testid={`beleg-files-${doc.code}`}>
                        {files.map((f, i) => (
                            <div key={f.uploaded_at || i} className="flex items-center gap-1.5 text-[11px] text-gray-600" data-testid={`beleg-file-${doc.code}-${i}`}>
                                <span className="font-bold text-gray-400 shrink-0">Seite {i + 1}</span>
                                <a href={`${PUBLIC_BASE}${f.url}`} target="_blank" rel="noopener noreferrer" className="text-sky-700 hover:underline truncate max-w-[180px]">{f.name || "Datei"}</a>
                                {f.quality?.pending && <Loader2 className="w-3 h-3 animate-spin text-gray-400 shrink-0" title="KI prüft…" />}
                                {f.quality?.checked && f.quality.ok === false && <AlertCircle className="w-3 h-3 text-red-500 shrink-0" title={f.quality.issues?.[0] || f.quality.message || "KI: bitte prüfen"} />}
                                <button type="button" onClick={() => doDelete(f.uploaded_at)} disabled={busy} className="text-red-500 hover:text-red-700 shrink-0" title="Diese Seite entfernen" data-testid={`beleg-file-delete-${doc.code}-${i}`}>
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {/* Jul-2026 (User-Wunsch) — KI-Prüfstatus links vom QR-Code / den Aktionen. */}
            {doc.uploaded && doc.quality?.pending && (
                <span className="inline-flex items-center gap-1 shrink-0 text-[10px] font-bold text-gray-500 bg-gray-100 border border-gray-200 rounded-sm px-1.5 py-0.5" title="KI-Prüfung läuft im Hintergrund — Sie können parallel weitere Belege hochladen." data-testid={`beleg-ai-pending-${doc.code}`}>
                    <Loader2 className="w-3 h-3 animate-spin" /> KI prüft…
                </span>
            )}
            {doc.uploaded && doc.quality?.checked && doc.quality.ok === false && (
                <span className="inline-flex items-center gap-1 shrink-0 text-[10px] font-bold text-red-700 bg-red-50 border border-red-300 rounded-sm px-1.5 py-0.5 max-w-[220px]"
                    title={`${doc.quality.message || "Dokument unleserlich oder ohne erkennbaren Zusammenhang."}${doc.quality.issues?.length ? " — " + doc.quality.issues.join("; ") : ""}`}
                    data-testid={`beleg-ai-warning-${doc.code}`}>
                    <AlertCircle className="w-3 h-3 shrink-0" /> <span className="truncate">KI: {doc.quality.issues?.[0] || "bitte prüfen"}</span>
                </span>
            )}
            {doc.uploaded && doc.quality?.checked && doc.quality.ok !== false && (
                <span className="inline-flex items-center gap-1 shrink-0 text-[10px] font-bold text-emerald-700" title="KI-Prüfung bestanden (lesbar, passendes Dokument)" data-testid={`beleg-ai-ok-${doc.code}`}>
                    <Sparkles className="w-3 h-3" /> KI ✓
                </span>
            )}
            {/* Jun-2026 (User-Wunsch) — kleiner QR je Dokument: Kunde scannt im Laden
                mit dem Handy und landet direkt bei genau diesem Beleg-Upload. */}
            {canQr && (
                <div className="hidden sm:flex flex-col items-center shrink-0" title="Kunde scannt mit dem Handy → öffnet das Portal direkt bei diesem Dokument. Mit der Maus darüberfahren = vergrößern zum Scannen." data-testid={`beleg-qr-${doc.code}`}>
                    {/* Jul-2026 (User-Wunsch): QR vergrößert sich beim Überfahren, damit er gut scannbar ist. */}
                    <div className="p-1 bg-white border border-gray-200 rounded relative transition-transform duration-200 ease-out hover:scale-[3] hover:z-[60] hover:shadow-2xl origin-right cursor-zoom-in">
                        <QRCodeSVG value={qrValue} size={40} level="M" />
                    </div>
                    <span className="text-[8px] text-gray-400 flex items-center gap-0.5 mt-0.5"><Smartphone className="w-2 h-2" /> Handy</span>
                </div>
            )}
            {doc.digitally_signed && !doc.uploaded ? (
                <span className="inline-flex items-center gap-1 shrink-0 text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-300 rounded-sm px-2 py-1" data-testid={`beleg-signed-${doc.code}`}>
                    <FileSignature className="w-3.5 h-3.5" /> Digital signiert
                </span>
            ) : doc.uploaded ? (
                <div className="flex items-center gap-1 shrink-0">
                    {/* Jul-2026 (Testfix) — weitere Seite/Foto anhängen. */}
                    <Button type="button" size="sm" onClick={() => camRef.current?.click()} disabled={busy} className="sm:hidden h-8 text-xs bg-orange-500 hover:bg-orange-600 text-white" title="Weitere Seite fotografieren" data-testid={`beleg-addpage-cam-${doc.code}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Camera className="w-3.5 h-3.5 mr-1" /> + Foto</>}
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} className="h-8 text-xs border-2" title="Weitere Seite / Datei zu diesem Beleg hinzufügen" data-testid={`beleg-addpage-${doc.code}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5" /> Seite</>}
                    </Button>
                    <a href={`${PUBLIC_BASE}${doc.url}`} target="_blank" rel="noopener noreferrer" data-testid={`beleg-view-${doc.code}`}>
                        <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-2"><ExternalLink className="w-3.5 h-3.5" /></Button>
                    </a>
                    <Button type="button" size="sm" variant="outline" onClick={() => doDelete()} disabled={busy} className="h-8 text-xs border-2 text-red-600 hover:bg-red-50" title="Alle Seiten dieses Belegs zurücksetzen" data-testid={`beleg-delete-${doc.code}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                </div>
            ) : (
                <>
                    <Button type="button" size="sm" onClick={() => camRef.current?.click()} disabled={busy} className="sm:hidden h-8 text-xs bg-orange-500 hover:bg-orange-600 text-white shrink-0" data-testid={`beleg-camera-${doc.code}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Camera className="w-3.5 h-3.5 mr-1" /> Foto</>}
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} className="h-8 text-xs border-2 shrink-0" data-testid={`beleg-upload-${doc.code}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" /> Hochladen</>}
                    </Button>
                </>
            )}
        </div>
    );
}

export function UploadBelegeModal({ listingId, onBack, onClose, onSaved }) {
    const gearsOk = useGearsVisible();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [sending, setSending] = useState(false);
    const [reminding, setReminding] = useState(false);
    const [contract, setContract] = useState(null);
    // Jun-2026 (User-Wunsch) — editierbarer E-Mail-Begleittext (Erst-Mail & Erinnerung).
    // Der Portal-Link bleibt serverseitig fix und ist NICHT editierbar.
    const [showMail, setShowMail] = useState(false);
    const [mailText, setMailText] = useState("");
    const [aiWriting, setAiWriting] = useState(false);

    // Jul-2026 (User-Report): Standardtext vorbefüllen statt leerem Feld.
    const openMailEditor = () => {
        setShowMail((v) => {
            if (!v && !mailText.trim() && data?.default_message) setMailText(data.default_message);
            return !v;
        });
    };

    // Jul-2026 (User-Wunsch): KI-Assistent formuliert den Begleittext.
    const aiSuggest = async () => {
        setAiWriting(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/belege/suggest-message`, {});
            if (r.data?.text) { setMailText(r.data.text); toast.success("KI-Textvorschlag eingefügt — bitte prüfen"); }
        } catch (e) { toast.error(e.response?.data?.detail || "KI-Vorschlag fehlgeschlagen"); }
        finally { setAiWriting(false); }
    };

    // Jul-2026 (User-Wunsch): Gesamt-PDF (Vertrag + AGB + Datenschutz + Selbstauskunft + Belege).
    const [completeBusy, setCompleteBusy] = useState(false);
    const openCompletePdf = async () => {
        setCompleteBusy(true);
        try {
            const r = await api.get(`/listings/${listingId}/dispo/documents-bundle.pdf?include_uploads=1`, { responseType: "blob" });
            previewPdfResponse(r, "gesamt-unterlagen.pdf");
        } catch (e) {
            let msg = "Gesamt-PDF fehlgeschlagen";
            try { msg = JSON.parse(await e.response.data.text())?.detail || msg; } catch { /* noop */ }
            toast.error(msg);
        } finally { setCompleteBusy(false); }
    };

    const load = async () => {
        try {
            const [bel, con] = await Promise.all([
                api.get(`/listings/${listingId}/dispo/belege`).then((r) => r.data),
                api.get(`/listings/${listingId}/dispo/contract/status`).then((r) => r.data).catch(() => null),
            ]);
            setData(bel);
            setContract(con && con.contract_number ? con : null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Belegliste konnte nicht geladen werden");
        } finally { setLoading(false); }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingId]);

    // Jul-2026 (User-Wunsch): KI-Prüfung läuft im Hintergrund → Status
    // automatisch nachladen, solange eine Prüfung aussteht.
    useEffect(() => {
        const anyPending = (data?.documents || []).some((d) => d.uploaded && d.quality?.pending);
        if (!anyPending) return undefined;
        const t = setTimeout(() => { load(); }, 5000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const onChanged = async () => { await load(); await onSaved?.(); };

    // Jun-2026 (User-Wunsch · Msg 509) — expliziter „Aktualisieren"-Button.
    const refresh = async () => {
        setRefreshing(true);
        try { await load(); await onSaved?.(); toast.success("Beleg-Status aktualisiert"); }
        finally { setRefreshing(false); }
    };
    const contractSigned = contract?.skribble_status === "signed";
    const contractPdf = `${PUBLIC_BASE}/api/listings/${listingId}/dispo/contract.pdf?ts=${Date.now()}`;

    const sendLink = async (channel = "email") => {
        setSending(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/belege/send-link`, { custom_message: mailText.trim() || undefined, channel });
            const chLabel = { email: "E-Mail", sms: "SMS", whatsapp: "WhatsApp" }[channel] || "E-Mail";
            toast.success(`Link per ${chLabel} an Kunden gesendet`);
            setData((p) => ({ ...p, portal_url: r.data.portal_url, sent_at: r.data.sent_at }));
        } catch (e) { toast.error(e.response?.data?.detail || "Link konnte nicht gesendet werden"); }
        finally { setSending(false); }
    };
    const remind = async () => {
        setReminding(true);
        try {
            const r = await api.post(`/listings/${listingId}/dispo/belege/remind`, { custom_message: mailText.trim() || undefined });
            toast.success("Erinnerung gesendet");
            setData((p) => ({ ...p, reminders: r.data.reminders }));
        } catch (e) { toast.error(e.response?.data?.detail || "Erinnerung fehlgeschlagen"); }
        finally { setReminding(false); }
    };
    const copyLink = () => {
        if (!data?.portal_url) return;
        navigator.clipboard?.writeText(data.portal_url).then(() => toast.success("Link kopiert")).catch(() => {});
    };

    const docs = data?.documents || [];
    const ungrouped = docs.filter((d) => !d.group);
    const groups = [...new Set(docs.filter((d) => d.group).map((d) => d.group))];
    const pct = data?.total ? Math.round((data.done / data.total) * 100) : 0;

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="dispo-modal-belege">
                <FeatureGuideTriangle guideKey="belege" />
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-[#111827]" /> Upload Belege
                        {gearsOk && <button type="button" onClick={() => setShowSettings((v) => !v)} title="Beleg-Anforderungen einstellen (wann wird was gebraucht)" className={`ml-1 p-1 rounded-sm border ${showSettings ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "text-gray-400 border-transparent hover:text-gray-700 hover:border-gray-200"}`} data-testid="belege-settings-gear">
                            <Settings className="w-4 h-4" />
                        </button>}
                        {(data?.customer_info?.first_name || data?.customer_info?.last_name) && (
                            <span className="ml-1 inline-flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-gray-100 border border-gray-200 rounded-sm px-2 py-1" data-testid="belege-customer-info">
                                <User className="w-3.5 h-3.5 text-gray-400" />
                                {[data.customer_info.first_name, data.customer_info.last_name].filter(Boolean).join(" ")}
                                {data.customer_info.birth_date && (
                                    <span className="text-gray-500 font-normal">
                                        · geb. {new Date(data.customer_info.birth_date).toLocaleDateString("de-DE")}
                                    </span>
                                )}
                            </span>
                        )}
                        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-6">Schritt 6 von 6</span>
                    </DialogTitle>
                </DialogHeader>

                {showSettings && <BelegeSettingsPanel onChanged={async () => { await load(); onSaved?.(); }} />}

                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center" data-testid="dispo-belege-loading"><Loader2 className="w-4 h-4 animate-spin" /> Belege werden geladen…</div>
                ) : (
                    <div className="space-y-4">
                        {/* Fortschritt */}
                        <div className="bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="dispo-belege-progress">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Belege ({data?.vehicle_label})</span>
                                <span className={`text-[11px] font-bold ${pct === 100 ? "text-emerald-600" : "text-gray-600"}`} data-testid="dispo-belege-count">{data?.done}/{data?.total} hochgeladen</span>
                            </div>
                            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full ${pct === 100 ? "bg-emerald-600" : "bg-orange-500"} transition-all duration-500`} style={{ width: `${pct}%` }} />
                            </div>
                            <p className="text-[11px] text-gray-500 mt-2">Es werden nur die Belege angezeigt, die für diese Zahlungsart &amp; dieses Fahrzeug zutreffen.</p>
                        </div>

                        {/* Jun-2026 (User-Wunsch · Msg 509) — Hinweis: Kaufvertrag & Datenschutz
                            sind bereits digital signiert (keine erneute Unterschrift nötig). */}
                        {contractSigned && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 flex items-start gap-2" data-testid="dispo-belege-contract-signed">
                                <FileSignature className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                                <div className="text-[12px] text-emerald-800 flex-1">
                                    <span className="font-bold">Kaufvertrag &amp; Datenschutzhinweise wurden bereits digital signiert</span>
                                    <span className="block text-emerald-700">— eine erneute Unterschrift dieser Dokumente ist nicht erforderlich.</span>
                                </div>
                                <a href={contractPdf} target="_blank" rel="noopener noreferrer" data-testid="dispo-belege-contract-link">
                                    <Button type="button" size="sm" variant="outline" className="h-8 text-xs border-2 shrink-0"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Kaufvertrag</Button>
                                </a>
                            </div>
                        )}

                        {/* Kunden-Upload-Portal: Link senden + manuelle Erinnerung */}
                        <div className="border border-gray-200 rounded-sm p-3 bg-white" data-testid="dispo-belege-portal">
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5 mb-2"><Link2 className="w-3.5 h-3.5" /> Kunden-Upload-Portal</div>
                            {!data?.customer_ready ? (
                                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 flex items-start gap-1.5" data-testid="dispo-belege-customer-missing">
                                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Bitte zuerst <strong>Nachname</strong> und <strong>Geburtsdatum</strong> in den Kundendaten erfassen (Sicherheitsabfrage).
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {data?.portal_url && (
                                        <div className="flex flex-col sm:flex-row items-start gap-3">
                                            <div className="flex-1 w-full">
                                                <div className="flex items-center gap-1.5">
                                                    <input readOnly value={data.portal_url} className="flex-1 text-[11px] font-mono bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 text-gray-600" data-testid="dispo-belege-portal-url" />
                                                    <Button type="button" size="sm" variant="outline" onClick={copyLink} className="h-8 border-2" data-testid="dispo-belege-copy"><Copy className="w-3.5 h-3.5" /></Button>
                                                </div>
                                                <p className="text-[10px] text-gray-500 mt-1 flex items-center gap-1"><QrCode className="w-3 h-3" /> QR im Laden zeigen → Kunde scannt &amp; lädt Unterlagen direkt vom Handy hoch.</p>
                                            </div>
                                            <div className="flex flex-col items-center shrink-0" data-testid="dispo-belege-portal-qr">
                                                <div className="p-1.5 bg-white border border-gray-200 rounded relative transition-transform duration-200 ease-out hover:scale-[1.8] hover:z-[60] hover:shadow-2xl origin-top-right cursor-zoom-in" title="Mit der Maus darüberfahren = vergrößern zum Scannen">
                                                    <QRCodeSVG value={data.portal_url} size={96} level="M" />
                                                </div>
                                                <span className="text-[9px] text-gray-400 mt-0.5">Portal-QR</span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Button type="button" size="sm" variant="outline" onClick={() => sendLink("email")} disabled={sending} className="h-8 text-xs border-2" data-testid="dispo-belege-send-link">
                                            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> {data?.sent_at ? "E-Mail erneut senden" : "Link per E-Mail senden"}</>}
                                        </Button>
                                        <Button type="button" size="sm" variant="outline" onClick={() => sendLink("whatsapp")} disabled={sending} className="h-8 text-xs border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="dispo-belege-send-whatsapp">
                                            <Send className="w-3.5 h-3.5 mr-1" /> WhatsApp
                                        </Button>
                                        <Button type="button" size="sm" variant="outline" onClick={() => sendLink("sms")} disabled={sending} className="h-8 text-xs border-2 border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="dispo-belege-send-sms">
                                            <Send className="w-3.5 h-3.5 mr-1" /> SMS
                                        </Button>
                                        {data?.portal_url && (
                                            <a
                                                href={`https://wa.me/?text=${encodeURIComponent(`Bitte laden Sie Ihre Unterlagen bequem am Handy hoch: ${data.portal_url}`)}`}
                                                target="_blank" rel="noopener noreferrer"
                                                className="h-8 inline-flex items-center rounded-sm border-2 border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 text-xs font-bold"
                                                data-testid="dispo-belege-wa-link"
                                                title="Öffnet WhatsApp mit vorausgefülltem Text — Sie wählen den Kontakt und senden selbst (ohne Twilio)."
                                            >
                                                <Send className="w-3.5 h-3.5 mr-1" /> WhatsApp öffnen
                                            </a>
                                        )}
                                        <Button type="button" size="sm" variant="outline" onClick={remind} disabled={reminding || !data?.sent_at} className="h-8 text-xs border-2" data-testid="dispo-belege-remind">
                                            {reminding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Bell className="w-3.5 h-3.5 mr-1" /> Erinnerung senden</>}
                                        </Button>
                                        <button type="button" onClick={openMailEditor} className="text-[11px] font-bold text-[#111827] hover:underline inline-flex items-center gap-1" data-testid="dispo-belege-mail-toggle">
                                            <Info className="w-3.5 h-3.5" /> E-Mail-Text einsehen / ändern
                                        </button>
                                    </div>
                                    <div className="text-[10px] text-gray-500 mt-1" data-testid="dispo-belege-channel-hint">WhatsApp/SMS senden den Portal-Link direkt aufs Handy/Tablet des Kunden (Handynummer erforderlich). Versand aktuell im Demo-Modus (gemockt).</div>
                                    {showMail && (
                                        <div className="mt-1.5 space-y-1.5" data-testid="dispo-belege-mail-editor">
                                            <Textarea value={mailText} onChange={(e) => setMailText(e.target.value)} rows={10} className="text-[13px] leading-relaxed bg-white border-2 border-orange-300 focus:border-orange-500 shadow-sm" placeholder="Begleittext für Erst-E-Mail & Erinnerung (optional). Leer lassen = Standardtext." data-testid="dispo-belege-mail-text" />
                                            <Button type="button" size="sm" variant="outline" onClick={aiSuggest} disabled={aiWriting} className="h-7 text-[11px] border-2" data-testid="dispo-belege-mail-ai">
                                                {aiWriting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                                                KI: Text formulieren
                                            </Button>
                                            <div className="text-[10px] text-gray-600 bg-white border border-gray-200 rounded-sm px-2 py-1.5 flex items-start gap-1.5" data-testid="dispo-belege-mail-fixed-note">
                                                <Info className="w-3 h-3 mt-0.5 shrink-0 text-sky-500" />
                                                <span>Dieser Text wird sowohl für die <b>Erst-E-Mail</b> als auch für <b>Erinnerungen</b> verwendet. Der <b>Portal-Link</b> (Button) sowie die Sicherheitsabfrage werden automatisch &amp; <b>unveränderbar</b> angehängt.</span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="text-[11px] text-gray-500" data-testid="dispo-belege-sent-info">
                                        {data?.sent_at ? `Gesendet am ${fmtDateDE(data.sent_at)}` : "Noch nicht gesendet."}
                                        {data?.reminders?.length ? ` · ${data.reminders.length} Erinnerung(en)` : ""}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Upload benötigter Unterlagen (Kunde & Verkäufer zusammengeführt) */}
                        {docs.length > 0 && (
                            <div data-testid="dispo-belege-customer-group">
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5 mb-2"><User className="w-3.5 h-3.5" /> Upload benötigter Unterlagen</div>
                                <div className="space-y-1.5">
                                    {ungrouped.map((d) => <BelegRow key={d.code} listingId={listingId} doc={d} onChanged={onChanged} portalUrl={data?.portal_url} />)}
                                </div>
                                {groups.map((g) => (
                                    <div key={g} className="mt-3" data-testid={`dispo-belege-group-${g}`}>
                                        <div className="text-[11px] font-bold uppercase tracking-wider text-orange-700 mb-1.5">{g}</div>
                                        <div className="space-y-1.5">
                                            {docs.filter((d) => d.group === g).map((d) => <BelegRow key={d.code} listingId={listingId} doc={d} onChanged={onChanged} portalUrl={data?.portal_url} />)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* „Im Original benötigt"-Hinweise aus GW-/Vertrags-/Zulassungs-Checklisten */}
                        {data?.original_required?.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="dispo-belege-original-required">
                                <div className="text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1.5 mb-2"><AlertCircle className="w-3.5 h-3.5" /> Zusätzlich im Original benötigt</div>
                                <ul className="space-y-1">
                                    {data.original_required.map((h, i) => (
                                        h.done ? (
                                            <li key={i} className="text-[12px] text-emerald-700 flex items-start gap-1.5" data-testid="dispo-belege-original-done">
                                                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                                                <span>
                                                    <span className="font-semibold">{h.label}</span>
                                                    <span className="ml-1 text-red-600 text-[10px] font-bold uppercase tracking-wider">erledigt{h.done_date ? ` · ${String(h.done_date).slice(0, 10).split("-").reverse().join(".")}` : ""}</span>
                                                    <span className="text-emerald-500 text-[10px]"> · {h.source}</span>
                                                </span>
                                            </li>
                                        ) : (
                                            <li key={i} className="text-[12px] text-amber-800 flex items-start gap-1.5"><Info className="w-3 h-3 mt-0.5 shrink-0" /><span><span className="font-semibold">{h.label}</span> <span className="text-amber-600 text-[10px]">· {h.source}</span></span></li>
                                        )
                                    ))}
                                </ul>
                            </div>
                        )}

                        <p className="text-[11px] text-gray-400">Hochgeladene Belege werden automatisch am Fahrzeug archiviert.</p>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <DispoBackButton onBack={onBack} />
                    {/* Jul-2026 (User-Wunsch): Gesamt-PDF zur Prüfung — Kaufvertrag, AGB,
                        Datenschutz, Selbstauskunft + alle hochgeladenen Belege in EINEM PDF. */}
                    <Button variant="outline" onClick={openCompletePdf} disabled={completeBusy} className="border-2 border-[#111827] font-bold" data-testid="dispo-belege-complete-pdf">
                        {completeBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1" /> Gesamt-PDF (Prüfansicht)</>}
                    </Button>
                    <Button variant="outline" onClick={refresh} disabled={refreshing || loading} className="border-2" data-testid="dispo-belege-refresh">
                        {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren</>}
                    </Button>
                    <Button variant="outline" onClick={onClose} data-testid="dispo-belege-close">Schließen</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
